import { useEffect, useId, useState } from 'react';

import { Button, Icon } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Input, SearchField, Select } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { dados } from '@/data/porta';
import type { ContatoParaEscolha, Procedimento } from '@/data/tipos';

export interface NovaReservaModalProps {
    onFechar: () => void;
    onReservada: () => void;
}

/**
 * Nova reserva — reservar à mão os insumos de um procedimento.
 *
 * A reserva normal nasce da proposta aceita; esta é a de quem marca a sessão
 * antes de a proposta existir, ou separa o material de um encaixe. O paciente é
 * opcional, a origem diz de onde veio, e o estoque que não cobre não impede a
 * reserva: ela nasce marcada, e a tela avisa antes — recusar esconderia o
 * problema, reservar calado deixaria marcar o que não dá para executar.
 */
export function NovaReservaModal({ onFechar, onReservada }: NovaReservaModalProps) {
    const aviso = useAviso();
    const idProcedimento = useId();
    const idOrigem = useId();
    const idBusca = useId();

    const [procedimentos, setProcedimentos] = useState<Procedimento[] | null>(null);
    const [erroDoCatalogo, setErroDoCatalogo] = useState<string | null>(null);
    const [procedimentoId, setProcedimentoId] = useState('');
    const [origem, setOrigem] = useState('Reserva manual');
    const [paciente, setPaciente] = useState<{ id: string; nome: string } | null>(null);
    const [busca, setBusca] = useState('');
    const [resultados, setResultados] = useState<ContatoParaEscolha[] | null>(null);
    const [erro, setErro] = useState<string | null>(null);
    const [salvando, setSalvando] = useState(false);

    useEffect(() => {
        let vivo = true;
        dados().procedimentos({ porPagina: 500 })
            .then((carga) => { if (vivo) setProcedimentos(carga.itens.filter((p) => p.ativo && p.insumos.length > 0)); })
            .catch((e: unknown) => { if (vivo) setErroDoCatalogo(e instanceof Error ? e.message : 'Não foi possível carregar os procedimentos.'); });
        return () => { vivo = false; };
    }, []);

    useEffect(() => {
        if (paciente || busca.trim().length < 2) {
            setResultados(null);
            return;
        }
        let vivo = true;
        const t = window.setTimeout(() => {
            dados().buscarContatos(busca)
                .then((r) => { if (vivo) setResultados(r); })
                .catch((e: unknown) => { if (vivo) setErro(`A busca falhou: ${e instanceof Error ? e.message : 'tente de novo'}.`); });
        }, 250);
        return () => { vivo = false; window.clearTimeout(t); };
    }, [busca, paciente]);

    const escolhido = procedimentos?.find((p) => p.id === procedimentoId);
    const faltando = escolhido ? escolhido.insumos.filter((i) => i.disponivel < i.embalagens).length : 0;

    const reservar = async () => {
        if (!escolhido) {
            setErro('Escolha o procedimento.');
            return;
        }
        setSalvando(true);
        setErro(null);
        try {
            const reserva = await dados().reservarInsumos(escolhido.id, {
                origem: origem.trim() || 'Reserva manual',
                ...(paciente ? { paciente: paciente.nome, contatoId: paciente.id } : { paciente: 'Sem paciente' }),
            });
            aviso(reserva.status === 'insuficiente'
                ? { title: 'Reservado com insumo insuficiente', description: `${escolhido.nome} · o estoque não cobre uma execução`, tone: 'warning' }
                : { title: 'Insumos reservados', description: escolhido.nome });
            onReservada();
            onFechar();
        } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não foi possível reservar.');
        } finally {
            setSalvando(false);
        }
    };

    return (
        <Modal
            open width={560} onClose={onFechar}
            title="Nova reserva"
            description="Separa os insumos de um procedimento antes da execução."
            dirty={Boolean(procedimentoId) || Boolean(paciente)} onSave={() => { void reservar(); }} saveLabel="Reservar e fechar"
            footer={(
                <>
                    <Button variant="secondary" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={() => { void reservar(); }} disabled={!escolhido || salvando}>{salvando ? 'Reservando…' : 'Reservar'}</Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                {erroDoCatalogo && <Alert tone="danger" title="Os procedimentos não carregaram" dismissible={false}>{erroDoCatalogo}</Alert>}
                {erro && <Alert tone="danger" title="Não foi possível reservar" dismissible={false}>{erro}</Alert>}

                <FormField label="Procedimento" htmlFor={idProcedimento} required hint="Só os que consomem insumo do estoque">
                    <Select
                        id={idProcedimento} value={procedimentoId} onChange={setProcedimentoId}
                        placeholder={procedimentos ? 'Escolha o procedimento' : 'Carregando procedimentos…'} disabled={!procedimentos}
                        options={(procedimentos ?? []).map((p) => ({ value: p.id, label: p.nome }))}
                    />
                </FormField>

                {escolhido && (
                    faltando > 0 ? (
                        <Alert tone="warning" title="O estoque não cobre uma execução" dismissible={false}>
                            {faltando === 1 ? 'Um insumo está' : `${faltando} insumos estão`} abaixo do necessário. A reserva é criada marcada como insuficiente.
                        </Alert>
                    ) : (
                        <p style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: 'var(--text-secondary)' }}>
                            <Icon name="package-check" size={14} />
                            {escolhido.insumos.length === 1 ? 'Um insumo' : `${escolhido.insumos.length} insumos`} com saldo para uma execução.
                        </p>
                    )
                )}

                <FormField label="Paciente" htmlFor={idBusca} hint="Opcional — a reserva também serve para separar material sem paciente definido">
                    {paciente ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 42, padding: '0 6px 0 12px', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-control)', background: 'var(--surface)' }}>
                            <Icon name="user-round" size={15} style={{ color: 'var(--neutral-500)' }} />
                            <span style={{ flex: 1, minWidth: 0, fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>{paciente.nome}</span>
                            <Button size="sm" variant="ghost" onClick={() => { setPaciente(null); setBusca(''); }}>Trocar</Button>
                        </div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            <SearchField value={busca} width="100%" ariaLabel="Buscar paciente" placeholder="Buscar paciente por nome ou telefone" onChange={setBusca} />
                            {resultados && resultados.length > 0 && (
                                <ul aria-label="Contatos encontrados" style={{ listStyle: 'none', margin: 0, padding: 0, border: '1px solid var(--border)', borderRadius: 'var(--radius-md)', overflow: 'hidden' }}>
                                    {resultados.map((c) => (
                                        <li key={c.id} style={{ borderTop: '1px solid var(--border-subtle)' }}>
                                            <button
                                                type="button" onClick={() => { setPaciente({ id: c.id, nome: c.nome }); setResultados(null); }}
                                                style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 40, padding: '8px 12px', border: 0, background: 'var(--surface)', cursor: 'pointer', textAlign: 'left', fontFamily: 'var(--font-ui)', fontSize: 13.5, color: 'var(--text-primary)' }}
                                            >
                                                <span style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>{c.nome}</span>
                                                <span style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>{c.telefone}</span>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    )}
                </FormField>

                <FormField label="Origem" htmlFor={idOrigem} hint="O que aparece na reserva e na movimentação de saída">
                    <Input id={idOrigem} value={origem} onChange={setOrigem} placeholder="Ex.: Encaixe da tarde" />
                </FormField>
            </div>
        </Modal>
    );
}
